import { TeamType } from "@redotech/account-common/team-type";
import { LoadState } from "@redotech/react-util/load";
import { Tag } from "@redotech/redo-common-model-tags-common/tag";
import { FulfillmentGroupColumnKey } from "@redotech/redo-fulfillment-common/fulfillment-group-column-keys";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { PrintKind } from "@redotech/redo-fulfillment-group-common/fulfillment-print-status";
import { OMSUser } from "@redotech/redo-fulfillment-group-common/oms-user";
import { WeightFormat } from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import type { OutboundLabelSettings } from "@redotech/redo-fulfillment-settings/outbound-label-settings";
import { FulfillmentGroupUpdate } from "@redotech/redo-merchant-fulfillment-rpc/schema/update-fulfillment-groups";
import { Provider } from "@redotech/redo-order-common/provider";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { ColumnAlignment } from "@redotech/redo-web/table";
import { Column2 } from "@redotech/redo-web/table2";
import uniqBy from "lodash/uniqBy";
import { AddressVerificationColumn } from "./columns/address-verification-column";
import { AssignedPickerColumn } from "./columns/assigned-picker-column";
import { AssignedTotesColumn } from "./columns/assigned-totes-column";
import { AssignedUserColumn } from "./columns/assigned-user-column";
import { AutoMergeDisabledColumn } from "./columns/auto-merge-disabled-column";
import { BatchIdColumn } from "./columns/batch-id-column";
import { BinColumn } from "./columns/bin-column";
import { CommercialInvoiceSubmittedElectronicallyColumn } from "./columns/commercial-invoice-electronic-column";
import { TotalCostColumn, UnitCostColumn } from "./columns/cost-columns";
import { CustomDateColumn } from "./columns/custom-date-column";
import { CustomNumberColumn } from "./columns/custom-number-column";
import { CustomStringColumn } from "./columns/custom-string-column";
import { CustomerEmailColumn } from "./columns/customer-email-column";
import { CustomerIdColumn } from "./columns/customer-id-column";
import { CustomerNameColumn } from "./columns/customer-name-column";
import { CustomerOrderCountColumn } from "./columns/customer-order-count";
import { CustomerPhoneColumn } from "./columns/customer-phone-column";
import { DeliveryDateColumn } from "./columns/delivery-date-column";
import { DeliveryMethodColumn } from "./columns/delivery-method-column";
import { DeliveryStatusColumn } from "./columns/delivery-status-column";
import { DestinationCountryColumn } from "./columns/destination-country-column";
import { EstimatedDeliveryDateColumn } from "./columns/estimated-delivery-date-column";
import { FraudRiskColumn } from "./columns/fraud-risk-column";
import { FulfilledByColumn } from "./columns/fulfilled-by-column";
import { FulfillmentLocationColumn } from "./columns/fulfillment-location-column";
import { HoldNotesColumn } from "./columns/hold-notes-column";
import { InternalNotesColumn } from "./columns/internal-notes-column";
import { InventoryProjectionColumn } from "./columns/inventory-projection-column";
import { isPrintedColumn } from "./columns/is-printed-column";
import { ItemBarcodeColumn } from "./columns/item-barcode-column";
import { ItemNameColumn } from "./columns/item-name-column";
import { ItemSKUColumn } from "./columns/item-sku-column";
import { LabelPurchasedAtColumn } from "./columns/label-purchased-at-column";
import { LineItemCustomAttributesColumn } from "./columns/line-item-custom-attributes-column";
import {
  LotExpirationDateColumn,
  LotNumberColumn,
} from "./columns/lot-columns";
import { MarketplaceFulfillByColumn } from "./columns/marketplace-fulfill-by-column";
import { MarketplaceInternalNoteColumn } from "./columns/marketplace-internal-note-column";
import { MarketplaceNotificationStatusColumn } from "./columns/marketplace-notification-status-column";
import { MissingBinsColumn } from "./columns/missing-bins-column";
import { MissingPickItemsColumn } from "./columns/missing-pick-items-column";
import { NoteFromBuyerColumn } from "./columns/note-from-buyer";
import { NoteToBuyerColumn } from "./columns/note-to-buyer";
import { NumberOfLineItemsColumn } from "./columns/number-of-line-items-column";
import { OnHandInventoryColumn } from "./columns/on-hand-inventory-column";
import { OrderAgeColumn } from "./columns/order-age-column";
import { OrderDateColumn } from "./columns/order-date-column";
import { OrderItemsColumn } from "./columns/order-items-column";
import { OrderNameColumn } from "./columns/order-name-column";
import { OrderSubtotalColumn } from "./columns/order-subtotal-column";
import { OrderTotalColumn } from "./columns/order-total-column";
import { OrderWeightColumn } from "./columns/order-weight-column";
import { OriginStoreColumn } from "./columns/origin-store-column";
import { PackingSlipPrintedAtColumn } from "./columns/packing-slip-printed-at-column";
import { PaymentTypeColumn } from "./columns/payment-type-column";
import { PickSessionIdColumn } from "./columns/pick-session-id-column";
import { PickStartedOnColumn } from "./columns/pick-started-on-column";
import { PickStatusColumn } from "./columns/pick-status-column";
import { PickabilityColumn } from "./columns/pickability-column";
import { PickableBinCountColumn } from "./columns/pickable-bin-count-column";
import { PickedAtColumn } from "./columns/picked-at-column";
import { CustomerPoNumberColumn } from "./columns/po-number-column";
import { PostalCodeColumn } from "./columns/postal-code-column";
import { ProductBinAssignmentDateColumn } from "./columns/product-bin-assignment-date-column";
import { ProductCustomDateColumn } from "./columns/product-custom-date-column";
import { ProductCustomNumberColumn } from "./columns/product-custom-number-column";
import { ProductCustomStringColumn } from "./columns/product-custom-string-column";
import { ProductTagsColumn } from "./columns/product-tags-column";
import { ProductVariantColumn } from "./columns/product-variant-column";
import { RateColumn } from "./columns/rate-column";
import { RecipientNameColumn } from "./columns/recipient-name-column";
import { RequestedServiceColumn } from "./columns/requested-service-column";
import { ReturnTrackingNumberColumn } from "./columns/return-tracking-number-column";
import { ReturnTrackingStatusColumn } from "./columns/return-tracking-status-column";
import { SalesChannelColumn } from "./columns/sales-channel-column";
import { ScanVerifiedColumn } from "./columns/scan-verified-column";
import { ParcelSelectionColumn } from "./columns/selected-parcel-column";
import { SerialNumberColumn } from "./columns/serial-number-column";
import { ShipmentAdjustmentsColumn } from "./columns/shipment-adjustments-column";
import { ShipmentCreatedAtColumn } from "./columns/shipment-created-at-column";
import { ShipmentTagsColumn } from "./columns/shipment-tags-column";
import { ShippedDateColumn } from "./columns/shipped-date-column";
import { ShippingInsuranceColumn } from "./columns/shipping-insurance-column";
import { ShippingMarginColumn } from "./columns/shipping-margin-column";
import { ShippingPaidColumn } from "./columns/shipping-paid-column";
import { ShippingSelectionColumn } from "./columns/shipping-selection-column";
import { ShopifyOrderTagsColumn } from "./columns/shopify-order-tags-column";
import { SLADeliveryPromiseColumn } from "./columns/sla-column";
import { StateColumn as DestinationStateColumn } from "./columns/state-column";
import { StatusColumn } from "./columns/status-column";
import { StreetAddressColumn } from "./columns/street-address-column";
import { TaxTotalColumn } from "./columns/tax-total-column";
import { TrackingNumberColumn } from "./columns/tracking-number-column";
import { UsedParcelLearningColumn } from "./columns/used-parcel-learning-column";
import { UsedWeightLearningColumn } from "./columns/used-weight-learning-column";
import { USPSZoneColumn } from "./columns/usps-zone-column";
import { VariantCustomDateColumn } from "./columns/variant-custom-date-column";
import { VariantCustomNumberColumn } from "./columns/variant-custom-number-column";
import { VariantCustomStringColumn } from "./columns/variant-custom-string-column";
import { VendorsColumn } from "./columns/vendors-column";
import { WarehouseBinsColumn } from "./columns/warehouse-bins-column";
import * as ordersCss from "./outbound-labels.module.css";

// If a column is specific to a subset of providers, add it to all of them
function getProviderSpecificColumns(
  provider: Provider,
): Column2<FulfillmentGroupData>[] {
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
  switch (provider) {
    case Provider.BIGCOMMERCE:
      return [MarketplaceInternalNoteColumn];
    case Provider.EBAY:
      return [CustomerIdColumn];
    case Provider.AIMS360:
      return [MarketplaceInternalNoteColumn];
    case Provider.APPAREL_MAGIC:
      return [MarketplaceInternalNoteColumn];
    case Provider.CIN7_OMNI:
      return [MarketplaceInternalNoteColumn];
    case Provider.COMMENTSOLD:
      return [MarketplaceInternalNoteColumn];
    case Provider.TIKTOK:
      return [MarketplaceInternalNoteColumn, CustomerIdColumn];
    default:
      return [];
  }
}

export function getFulfillmentOrderColumns({
  allChecked,
  someChecked,
  toggleAllSelection,
  handleCheckClick,
  checkedSubItems,
  getRowCheckboxState,
  toggleSubItemSelection,
  availableTagsLoad,
  getAvailableTags,
  updateRowData,
  autoCleanAddresses,
  weightFormat,
  groupsWaitingForSplit = [],
  customMetadataSettings,
  customAttributesSettings,
  productCustomMetadataSettings,
  variantCustomMetadataSettings,
  teamProviders,
  teamType,
  wmsEnabled,
  inventoryProjectionEnabled,
  hideInputItems,
  users,
}: {
  allChecked: boolean;
  someChecked: boolean;
  toggleAllSelection: (selected: boolean) => void;
  handleCheckClick: (id: string, e: React.MouseEvent<HTMLDivElement>) => void;
  checkedSubItems: Map<string, Set<number>>;
  getRowCheckboxState: (rowId: string) => boolean | "indeterminate";
  toggleSubItemSelection: (rowId: string, subItemIndex: number) => void;
  availableTagsLoad: LoadState<Tag[] | undefined>;
  getAvailableTags: (signal?: AbortSignal) => void;
  updateRowData: (rowUpdates: FulfillmentGroupUpdate[]) => void;
  autoCleanAddresses: boolean;
  weightFormat: WeightFormat;
  groupsWaitingForSplit?: string[];
  customMetadataSettings: OutboundLabelSettings["customMetadata"];
  customAttributesSettings: OutboundLabelSettings["customAttributes"];
  productCustomMetadataSettings: OutboundLabelSettings["productCustomMetadata"];
  variantCustomMetadataSettings: OutboundLabelSettings["variantCustomMetadata"];
  teamProviders: readonly Provider[];
  teamType?: TeamType;
  wmsEnabled?: boolean;
  inventoryProjectionEnabled?: boolean;
  hideInputItems: boolean;
  users: OMSUser[];
}): Column2<FulfillmentGroupData>[] {
  // These base columns are always included
  const columns: Column2<FulfillmentGroupData>[] = [
    {
      alignment: ColumnAlignment.LEFT,
      key: FulfillmentGroupColumnKey.SELECTED,
      title: (
        <div className={ordersCss.checkboxContainer}>
          <RedoCheckbox
            dataTestId="group-table-header-checkbox"
            setValue={toggleAllSelection}
            size="sm"
            value={someChecked && !allChecked ? "indeterminate" : allChecked}
          />
        </div>
      ),
      renderNormalCell: ({ row, rowIndex }) => {
        const checkboxState = getRowCheckboxState(row._id);
        return (
          <div
            className={ordersCss.checkboxContainer}
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <RedoCheckbox
              dataTestId={`group-table-row-checkbox-${rowIndex}`}
              onClick={(e) => {
                handleCheckClick(row._id, e);
              }}
              setValue={() => {}}
              size="sm"
              value={checkboxState}
            />
          </div>
        );
      },
      width: 24,
      widthMode: { type: "fixed", width: "24px" },
      nonHidable: true,
      disableStickiness: true,
    },
    OrderNameColumn,
    CustomerNameColumn,
    CustomerEmailColumn,
    CustomerPhoneColumn,
    OrderDateColumn,
    ShipmentCreatedAtColumn,
    LabelPurchasedAtColumn,
    FulfilledByColumn,
    MarketplaceFulfillByColumn,
    OrderTotalColumn,
    OrderItemsColumn,
    OrderWeightColumn(weightFormat),
    ShipmentTagsColumn({ availableTagsLoad, getAvailableTags, updateRowData }),
    ShopifyOrderTagsColumn,
    ProductTagsColumn,
    ParcelSelectionColumn,
    DeliveryMethodColumn,
    ShippingSelectionColumn,
    TrackingNumberColumn,
    ReturnTrackingNumberColumn,
    ReturnTrackingStatusColumn,
    OrderAgeColumn,
    AssignedUserColumn,
    DestinationStateColumn,
    DestinationCountryColumn,
    RateColumn,
    ShippedDateColumn,
    AddressVerificationColumn,
    FraudRiskColumn,
    SLADeliveryPromiseColumn,
    ItemNameColumn({
      checkedSubItems,
      toggleSubItemSelection,
      groupsWaitingForSplit,
    }),
    ItemSKUColumn,
    ItemBarcodeColumn,
    UnitCostColumn,
    TotalCostColumn,
    LineItemCustomAttributesColumn,
    ProductVariantColumn,
    NumberOfLineItemsColumn,
    RequestedServiceColumn,
    ShippingPaidColumn,
    ShippingMarginColumn,
    ShipmentAdjustmentsColumn,
    NoteFromBuyerColumn(updateRowData),
    NoteToBuyerColumn(updateRowData),
    InternalNotesColumn(updateRowData),
    HoldNotesColumn,
    ScanVerifiedColumn({ users, hideInputItems }),
    SerialNumberColumn,
    FulfillmentLocationColumn,
    OnHandInventoryColumn,
    ...(inventoryProjectionEnabled ? [InventoryProjectionColumn] : []),
    StatusColumn,
    ...(wmsEnabled
      ? [
          PickabilityColumn,
          PickableBinCountColumn,
          WarehouseBinsColumn,
          ProductBinAssignmentDateColumn,
          PickStatusColumn,
          PickedAtColumn,
          PickStartedOnColumn,
          MissingPickItemsColumn({ hideInputItems }),
          AssignedPickerColumn,
          PickSessionIdColumn,
          AssignedTotesColumn,
          LotNumberColumn,
          LotExpirationDateColumn,
        ]
      : []),
    isPrintedColumn(PrintKind.PICK_LIST),
    isPrintedColumn(PrintKind.PACKING_SLIP),
    isPrintedColumn(PrintKind.LABEL),
    ShippingInsuranceColumn,
    SalesChannelColumn,
    ...(teamType === TeamType.FulfillmentService ? [OriginStoreColumn] : []),
    BatchIdColumn,
    VendorsColumn,
    UsedWeightLearningColumn,
    UsedParcelLearningColumn,
    PostalCodeColumn(autoCleanAddresses),
    CommercialInvoiceSubmittedElectronicallyColumn,
    USPSZoneColumn,
    BinColumn,
    DeliveryStatusColumn,
    EstimatedDeliveryDateColumn,
    DeliveryDateColumn,
    OrderSubtotalColumn,
    TaxTotalColumn,
    PackingSlipPrintedAtColumn,
    MissingBinsColumn,
    AutoMergeDisabledColumn,
    RecipientNameColumn,
    StreetAddressColumn,
    CustomerOrderCountColumn,
    PaymentTypeColumn,
    CustomerPoNumberColumn,
    MarketplaceNotificationStatusColumn,
  ];

  // These columns are specific to a subset of providers
  for (const provider of teamProviders) {
    columns.push(...getProviderSpecificColumns(provider));
  }

  // The rest of these columns are specific to the team
  if (customMetadataSettings?.customDate1) {
    columns.push(
      CustomDateColumn({
        title:
          customMetadataSettings.customDate1.displayName ||
          `${customMetadataSettings.customDate1.key}.${customMetadataSettings.customDate1.namespace}`,
        key: "customDate1",
      }),
    );
  }

  if (customMetadataSettings?.customDate2) {
    columns.push(
      CustomDateColumn({
        title:
          customMetadataSettings.customDate2.displayName ||
          `${customMetadataSettings.customDate2.key}.${customMetadataSettings.customDate2.namespace}`,
        key: "customDate2",
      }),
    );
  }

  if (customMetadataSettings?.customDate3) {
    columns.push(
      CustomDateColumn({
        title:
          customMetadataSettings.customDate3.displayName ||
          `${customMetadataSettings.customDate3.key}.${customMetadataSettings.customDate3.namespace}`,
        key: "customDate3",
      }),
    );
  }

  if (customMetadataSettings?.customString1) {
    columns.push(
      CustomStringColumn({
        title:
          customMetadataSettings.customString1.displayName ||
          `${customMetadataSettings.customString1.key}.${customMetadataSettings.customString1.namespace}`,
        key: "customString1",
      }),
    );
  }

  if (customMetadataSettings?.customString2) {
    columns.push(
      CustomStringColumn({
        title:
          customMetadataSettings.customString2.displayName ||
          `${customMetadataSettings.customString2.key}.${customMetadataSettings.customString2.namespace}`,
        key: "customString2",
      }),
    );
  }

  if (customMetadataSettings?.customString3) {
    columns.push(
      CustomStringColumn({
        title:
          customMetadataSettings.customString3.displayName ||
          `${customMetadataSettings.customString3.key}.${customMetadataSettings.customString3.namespace}`,
        key: "customString3",
      }),
    );
  }

  if (customMetadataSettings?.customNumber1) {
    columns.push(
      CustomNumberColumn({
        title:
          customMetadataSettings.customNumber1.displayName ||
          `${customMetadataSettings.customNumber1.key}.${customMetadataSettings.customNumber1.namespace}`,
        key: "customNumber1",
      }),
    );
  }

  if (customMetadataSettings?.customNumber2) {
    columns.push(
      CustomNumberColumn({
        title:
          customMetadataSettings.customNumber2.displayName ||
          `${customMetadataSettings.customNumber2.key}.${customMetadataSettings.customNumber2.namespace}`,
        key: "customNumber2",
      }),
    );
  }

  if (customMetadataSettings?.customNumber3) {
    columns.push(
      CustomNumberColumn({
        title:
          customMetadataSettings.customNumber3.displayName ||
          `${customMetadataSettings.customNumber3.key}.${customMetadataSettings.customNumber3.namespace}`,
        key: "customNumber3",
      }),
    );
  }

  // Custom Attributes columns
  if (customAttributesSettings?.customAttributeDate1) {
    columns.push(
      CustomDateColumn({
        title:
          customAttributesSettings.customAttributeDate1.displayName ||
          customAttributesSettings.customAttributeDate1.key,
        key: "customAttributeDate1",
      }),
    );
  }

  if (customAttributesSettings?.customAttributeDate2) {
    columns.push(
      CustomDateColumn({
        title:
          customAttributesSettings.customAttributeDate2.displayName ||
          customAttributesSettings.customAttributeDate2.key,
        key: "customAttributeDate2",
      }),
    );
  }

  if (customAttributesSettings?.customAttributeDate3) {
    columns.push(
      CustomDateColumn({
        title:
          customAttributesSettings.customAttributeDate3.displayName ||
          customAttributesSettings.customAttributeDate3.key,
        key: "customAttributeDate3",
      }),
    );
  }

  if (customAttributesSettings?.customAttributeString1) {
    columns.push(
      CustomStringColumn({
        title:
          customAttributesSettings.customAttributeString1.displayName ||
          customAttributesSettings.customAttributeString1.key,
        key: "customAttributeString1",
      }),
    );
  }

  if (customAttributesSettings?.customAttributeString2) {
    columns.push(
      CustomStringColumn({
        title:
          customAttributesSettings.customAttributeString2.displayName ||
          customAttributesSettings.customAttributeString2.key,
        key: "customAttributeString2",
      }),
    );
  }

  if (customAttributesSettings?.customAttributeString3) {
    columns.push(
      CustomStringColumn({
        title:
          customAttributesSettings.customAttributeString3.displayName ||
          customAttributesSettings.customAttributeString3.key,
        key: "customAttributeString3",
      }),
    );
  }

  if (customAttributesSettings?.customAttributeNumber1) {
    columns.push(
      CustomNumberColumn({
        title:
          customAttributesSettings.customAttributeNumber1.displayName ||
          customAttributesSettings.customAttributeNumber1.key,
        key: "customAttributeNumber1",
      }),
    );
  }

  if (customAttributesSettings?.customAttributeNumber2) {
    columns.push(
      CustomNumberColumn({
        title:
          customAttributesSettings.customAttributeNumber2.displayName ||
          customAttributesSettings.customAttributeNumber2.key,
        key: "customAttributeNumber2",
      }),
    );
  }

  if (customAttributesSettings?.customAttributeNumber3) {
    columns.push(
      CustomNumberColumn({
        title:
          customAttributesSettings.customAttributeNumber3.displayName ||
          customAttributesSettings.customAttributeNumber3.key,
        key: "customAttributeNumber3",
      }),
    );
  }

  // Product Custom Metadata columns
  if (productCustomMetadataSettings?.productCustomDate1) {
    columns.push(
      ProductCustomDateColumn({
        title:
          productCustomMetadataSettings.productCustomDate1.displayName ||
          `${productCustomMetadataSettings.productCustomDate1.key}.${productCustomMetadataSettings.productCustomDate1.namespace}`,
        key: "productCustomDate1",
      }),
    );
  }

  if (productCustomMetadataSettings?.productCustomDate2) {
    columns.push(
      ProductCustomDateColumn({
        title:
          productCustomMetadataSettings.productCustomDate2.displayName ||
          `${productCustomMetadataSettings.productCustomDate2.key}.${productCustomMetadataSettings.productCustomDate2.namespace}`,
        key: "productCustomDate2",
      }),
    );
  }

  if (productCustomMetadataSettings?.productCustomDate3) {
    columns.push(
      ProductCustomDateColumn({
        title:
          productCustomMetadataSettings.productCustomDate3.displayName ||
          `${productCustomMetadataSettings.productCustomDate3.key}.${productCustomMetadataSettings.productCustomDate3.namespace}`,
        key: "productCustomDate3",
      }),
    );
  }

  if (productCustomMetadataSettings?.productCustomString1) {
    columns.push(
      ProductCustomStringColumn({
        title:
          productCustomMetadataSettings.productCustomString1.displayName ||
          `${productCustomMetadataSettings.productCustomString1.key}.${productCustomMetadataSettings.productCustomString1.namespace}`,
        key: "productCustomString1",
      }),
    );
  }

  if (productCustomMetadataSettings?.productCustomString2) {
    columns.push(
      ProductCustomStringColumn({
        title:
          productCustomMetadataSettings.productCustomString2.displayName ||
          `${productCustomMetadataSettings.productCustomString2.key}.${productCustomMetadataSettings.productCustomString2.namespace}`,
        key: "productCustomString2",
      }),
    );
  }

  if (productCustomMetadataSettings?.productCustomString3) {
    columns.push(
      ProductCustomStringColumn({
        title:
          productCustomMetadataSettings.productCustomString3.displayName ||
          `${productCustomMetadataSettings.productCustomString3.key}.${productCustomMetadataSettings.productCustomString3.namespace}`,
        key: "productCustomString3",
      }),
    );
  }

  if (productCustomMetadataSettings?.productCustomNumber1) {
    columns.push(
      ProductCustomNumberColumn({
        title:
          productCustomMetadataSettings.productCustomNumber1.displayName ||
          `${productCustomMetadataSettings.productCustomNumber1.key}.${productCustomMetadataSettings.productCustomNumber1.namespace}`,
        key: "productCustomNumber1",
      }),
    );
  }

  if (productCustomMetadataSettings?.productCustomNumber2) {
    columns.push(
      ProductCustomNumberColumn({
        title:
          productCustomMetadataSettings.productCustomNumber2.displayName ||
          `${productCustomMetadataSettings.productCustomNumber2.key}.${productCustomMetadataSettings.productCustomNumber2.namespace}`,
        key: "productCustomNumber2",
      }),
    );
  }

  if (productCustomMetadataSettings?.productCustomNumber3) {
    columns.push(
      ProductCustomNumberColumn({
        title:
          productCustomMetadataSettings.productCustomNumber3.displayName ||
          `${productCustomMetadataSettings.productCustomNumber3.key}.${productCustomMetadataSettings.productCustomNumber3.namespace}`,
        key: "productCustomNumber3",
      }),
    );
  }

  // Variant Custom Metadata columns
  if (variantCustomMetadataSettings?.variantCustomDate1) {
    columns.push(
      VariantCustomDateColumn({
        title:
          variantCustomMetadataSettings.variantCustomDate1.displayName ||
          `${variantCustomMetadataSettings.variantCustomDate1.key}.${variantCustomMetadataSettings.variantCustomDate1.namespace}`,
        key: "variantCustomDate1",
      }),
    );
  }

  if (variantCustomMetadataSettings?.variantCustomDate2) {
    columns.push(
      VariantCustomDateColumn({
        title:
          variantCustomMetadataSettings.variantCustomDate2.displayName ||
          `${variantCustomMetadataSettings.variantCustomDate2.key}.${variantCustomMetadataSettings.variantCustomDate2.namespace}`,
        key: "variantCustomDate2",
      }),
    );
  }

  if (variantCustomMetadataSettings?.variantCustomDate3) {
    columns.push(
      VariantCustomDateColumn({
        title:
          variantCustomMetadataSettings.variantCustomDate3.displayName ||
          `${variantCustomMetadataSettings.variantCustomDate3.key}.${variantCustomMetadataSettings.variantCustomDate3.namespace}`,
        key: "variantCustomDate3",
      }),
    );
  }

  if (variantCustomMetadataSettings?.variantCustomString1) {
    columns.push(
      VariantCustomStringColumn({
        title:
          variantCustomMetadataSettings.variantCustomString1.displayName ||
          `${variantCustomMetadataSettings.variantCustomString1.key}.${variantCustomMetadataSettings.variantCustomString1.namespace}`,
        key: "variantCustomString1",
      }),
    );
  }

  if (variantCustomMetadataSettings?.variantCustomString2) {
    columns.push(
      VariantCustomStringColumn({
        title:
          variantCustomMetadataSettings.variantCustomString2.displayName ||
          `${variantCustomMetadataSettings.variantCustomString2.key}.${variantCustomMetadataSettings.variantCustomString2.namespace}`,
        key: "variantCustomString2",
      }),
    );
  }

  if (variantCustomMetadataSettings?.variantCustomString3) {
    columns.push(
      VariantCustomStringColumn({
        title:
          variantCustomMetadataSettings.variantCustomString3.displayName ||
          `${variantCustomMetadataSettings.variantCustomString3.key}.${variantCustomMetadataSettings.variantCustomString3.namespace}`,
        key: "variantCustomString3",
      }),
    );
  }

  if (variantCustomMetadataSettings?.variantCustomNumber1) {
    columns.push(
      VariantCustomNumberColumn({
        title:
          variantCustomMetadataSettings.variantCustomNumber1.displayName ||
          `${variantCustomMetadataSettings.variantCustomNumber1.key}.${variantCustomMetadataSettings.variantCustomNumber1.namespace}`,
        key: "variantCustomNumber1",
      }),
    );
  }

  if (variantCustomMetadataSettings?.variantCustomNumber2) {
    columns.push(
      VariantCustomNumberColumn({
        title:
          variantCustomMetadataSettings.variantCustomNumber2.displayName ||
          `${variantCustomMetadataSettings.variantCustomNumber2.key}.${variantCustomMetadataSettings.variantCustomNumber2.namespace}`,
        key: "variantCustomNumber2",
      }),
    );
  }

  if (variantCustomMetadataSettings?.variantCustomNumber3) {
    columns.push(
      VariantCustomNumberColumn({
        title:
          variantCustomMetadataSettings.variantCustomNumber3.displayName ||
          `${variantCustomMetadataSettings.variantCustomNumber3.key}.${variantCustomMetadataSettings.variantCustomNumber3.namespace}`,
        key: "variantCustomNumber3",
      }),
    );
  }

  return uniqBy(columns, "key");
}
